/* eslint-disable import/no-duplicates */
import React, { useCallback, useEffect, useState } from 'react';

import { format, parseISO } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';

import { Row, Col, Button } from 'react-bootstrap';
import { FaTrashAlt, FaPrint, FaReply } from 'react-icons/fa';

import { ContainerDados } from './styles';

import PedidoExames from './cadastro';
import getResponseErrors from '../../../utils/getResponseErrors';
import api from '../../../services/api';
import Loading from '../../../components/Loading';
import { useToast } from '../../../hooks/toast';
import { IPerfil, usePerfil } from '../../../hooks/perfil';

export interface IPedidoExames {
  PedCodigo: number;
  PedMedUsuCodigo: number;
  PedPacCodigo: number;
  PedPerCodigo: number;
  PedAgeCodigo: number;
  PedCliCodigo: number;
  PedFinalizado: boolean;
  created_at: Date;
  dataPedido: string;
  medico: {
    UsuCodigo: number;
    UsuNome: string;
  };
  paciente: {
    PacCodigo: number;
    PacNome: string;
  };
  perfil: {
    PerCodigo: number;
    PerNome: string;
  };
  itens: {
    ItmCodigo: number;
    ItmProCodigo: number;
  };
}

type PacienteProps = {
  pacCodigo: number;
  ageCodigo: number;
  cliCodigo: number;
};

const Pedidos: React.FC<PacienteProps> = ({
  pacCodigo,
  ageCodigo,
  cliCodigo,
}) => {
  const { addToast } = useToast();
  const { perfis, buscaPerfil } = usePerfil();
  const [loading, setLoading] = useState(false);
  const [showCadastro, setShowCadastro] = useState(true);
  const [pedido, setPedido] = useState<IPedidoExames[]>();

  useEffect(() => {
    if (!perfis.perfis) buscaPerfil();
  }, [perfis.perfis, buscaPerfil]);

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get<[IPedidoExames[], number]>(
          `/pedidoExames?PedAgeCodigo=${ageCodigo}`,
        );

        const dadosFormatados = response.data[0].map((ped: IPedidoExames) => {
          return {
            ...ped,
            dataPedido: format(
              parseISO(ped.created_at.toString()),
              'dd/MM/yyyy HH:mm',
              { locale: ptBR },
            ),
          };
        });
        setPedido(dadosFormatados);
        if (dadosFormatados.length > 0) setShowCadastro(false);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível buscar dados',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }
    if (ageCodigo && ageCodigo > 0) {
      loadDados();
    }
  }, [addToast, ageCodigo]);

  const handleDelete = useCallback(
    async (codigo: number) => {
      setLoading(true);
      try {
        await api.delete(`pedidoexames/${codigo}`);

        setPedido(state => {
          return state?.filter(ped => ped.PedCodigo !== codigo);
        });
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível excluir o registro',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const handleRegistroIncluido = useCallback(
    async (dados: IPedidoExames[]) => {
      try {
        const dadosFormatados = dados.map(ped => {
          const perfil: IPerfil | undefined = perfis.perfis.find(
            per => per.PerCodigo === ped.PedPerCodigo,
          );
          return {
            ...ped,
            perfil: {
              PerCodigo: perfil?.PerCodigo || 0,
              PerNome: perfil?.PerNome || '',
            },
            dataPedido: format(
              parseISO(ped.created_at.toString()),
              'dd/MM/yyyy HH:mm',
            ),
          };
        });

        setPedido(state => state?.concat(dadosFormatados));

        setShowCadastro(false);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível incluir o registro',
          type: 'error',
          description: mensagemErro,
        });
      }
    },
    [addToast, perfis.perfis],
  );

  return (
    <>
      {loading && <Loading backgroundBlack={false} />}
      <ContainerDados className="rounded">
        {showCadastro ? (
          <>
            <Button
              type="button"
              variant="outline-primary"
              size="sm"
              className="my-2"
              onClick={() => {
                setShowCadastro(false);
              }}
            >
              <FaReply size={16} /> Listar Pedidos
            </Button>
            <PedidoExames
              pacCodigo={pacCodigo}
              ageCodigo={ageCodigo}
              cliCodigo={cliCodigo}
              handleDadosSalvos={handleRegistroIncluido}
            />
          </>
        ) : (
          <Row>
            <Col>
              <div className="d-flex justify-content-between aling-items-center my-2">
                <h5>Pedidos de Exames</h5>
                <Button
                  type="button"
                  onClick={() => {
                    setShowCadastro(true);
                  }}
                  variant="primary"
                  size="sm"
                >
                  Adicionar
                </Button>
              </div>
              {pedido && pedido.length > 0 ? (
                <table
                  className="table table-hover table-sm table-font-small"
                  style={{ borderWidth: '0px' }}
                >
                  <thead>
                    <tr className="bg-light text-dark">
                      <th style={{ width: '115px' }}>Data/Hora</th>
                      <th>Perfil</th>
                      <th style={{ width: '60px' }}> - </th>
                      <th style={{ width: '60px' }}> - </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pedido.map((ped: IPedidoExames) => (
                      <tr
                        key={`pedido-${ped.PedCodigo}`}
                        style={{
                          fontSize: '.7rem',
                          height: '30px',
                        }}
                      >
                        <td>{ped.dataPedido}</td>
                        <td>{ped.perfil.PerNome}</td>
                        <td>
                          {ped.perfil.PerNome === 'CHECKUP' ? (
                            <a
                              href={`/impressaodocumento/${ped.PedCodigo}/pedidoexamenaoagendadocheckup`}
                              rel="noreferrer"
                              target="_blank"
                            >
                              <FaPrint size={16} />
                            </a>
                          ) : (
                            <a
                              href={`/impressaodocumento/${ped.PedCodigo}/pedidoexamenaoagendado`}
                              rel="noreferrer"
                              target="_blank"
                            >
                              <FaPrint size={16} />
                            </a>
                          )}
                        </td>
                        <td>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline-primary"
                            onClick={() => handleDelete(ped.PedCodigo)}
                          >
                            <FaTrashAlt size={16} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>Nenhum pedido gerado</p>
              )}
            </Col>
          </Row>
        )}
      </ContainerDados>
    </>
  );
};

export default Pedidos;
